import { useMemo } from 'react';
import { BusinessAccess } from './useBusinessAccessInvitations';
import { useAccessTypeLabelMap, useAccessStatusLabelMap } from './useAccessTypeOptions';
import {
  groupByAccessType,
  groupByStatus,
  calculateConversionRate,
  calculateAverageGuests,
  getTimeSeriesData,
  getHeatmapData,
  getTopGuests,
  groupByChannel,
  calculatePercentageChange,
} from '../utils/dashboardUtils';

export interface DashboardMetrics {
  totalAccess: number;
  pendingAccess: number;
  approvedAccess: number;
  inAccess: number;
  outAccess: number;
  rejectedAccess: number;
  cancelledAccess: number;
  conversionRate: number;
  averageGuests: number;
  accessTypeDistribution: ReturnType<typeof groupByAccessType>;
  statusDistribution: ReturnType<typeof groupByStatus>;
  channelDistribution: ReturnType<typeof groupByChannel>;
  topGuests: ReturnType<typeof getTopGuests>;
  timeSeriesData: ReturnType<typeof getTimeSeriesData>;
  heatmapData: ReturnType<typeof getHeatmapData>;
}

export interface ComparisonMetrics {
  totalChange: number;
  pendingChange: number;
  inChange: number;
  conversionRateChange: number;
}

/**
 * Hook para calcular métricas del dashboard
 */
export const useDashboardMetrics = (
  data: BusinessAccess[],
  previousData?: BusinessAccess[],
  granularity: 'hour' | 'day' | 'week' | 'month' = 'day'
): { metrics: DashboardMetrics; comparison?: ComparisonMetrics } => {
  const { map: accessTypeLabelMap } = useAccessTypeLabelMap();
  const { map: accessStatusLabelMap } = useAccessStatusLabelMap();

  const metrics = useMemo(() => {
    const totalAccess = data.length;
    const pendingAccess = data.filter(item => item.business_access_status_id === 1).length;
    const approvedAccess = data.filter(item => item.business_access_status_id === 2).length;
    const inAccess = data.filter(item => item.business_access_status_id === 3).length;
    const outAccess = data.filter(item => item.business_access_status_id === 6).length;
    const rejectedAccess = data.filter(item => item.business_access_status_id === 4).length;
    const cancelledAccess = data.filter(item => item.business_access_status_id === 5).length;

    return {
      totalAccess,
      pendingAccess,
      approvedAccess,
      inAccess,
      outAccess,
      rejectedAccess,
      cancelledAccess,
      conversionRate: calculateConversionRate(data),
      averageGuests: calculateAverageGuests(data),
      accessTypeDistribution: groupByAccessType(data, accessTypeLabelMap),
      statusDistribution: groupByStatus(data, accessStatusLabelMap),
      channelDistribution: groupByChannel(data),
      topGuests: getTopGuests(data, 10, accessTypeLabelMap),
      timeSeriesData: getTimeSeriesData(data, granularity),
      heatmapData: getHeatmapData(data),
    };
  }, [data, granularity, accessTypeLabelMap, accessStatusLabelMap]);

  const comparison = useMemo(() => {
    if (!previousData || previousData.length === 0) return undefined;

    const prevTotal = previousData.length;
    const prevPending = previousData.filter(item => item.business_access_status_id === 1).length;
    const prevIn = previousData.filter(item => item.business_access_status_id === 3).length;
    const prevConversionRate = calculateConversionRate(previousData);

    return {
      totalChange: calculatePercentageChange(metrics.totalAccess, prevTotal),
      pendingChange: calculatePercentageChange(metrics.pendingAccess, prevPending),
      inChange: calculatePercentageChange(metrics.inAccess, prevIn),
      conversionRateChange: calculatePercentageChange(metrics.conversionRate, prevConversionRate),
    };
  }, [previousData, metrics]);

  return { metrics, comparison };
};
